import React, { useEffect, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import SelectOptionComp from '@components/UI/SelectOption/Index.jsx';
import InputTextComp from '@components/UI/InputText/Index.jsx';
import { useFetchDataAuditores } from '@hooks/api/Usuario/useFetchDataAuditores.js';
import { useFetchDataTemplatesEmails } from '@hooks/api/TemplateEmails/useFetchDataTemplatesEmails.js';
import SweetAlert from '@components/Feedback/SweetAlert/Index.jsx';
import InputDatePickerComp from '@components/UI/InputDatePicker/Index.jsx';
import dayjs from 'dayjs';
import { useDisableIfStatusAndRole } from '@components/UI/DisableIfStatusAndRole/Index.jsx';
import { useSelector } from 'react-redux';
import { selectStatusById } from '@store/selectors/solicitacaoSelector';
import { perfisAdmin } from '@utils/auth/perfis';

function Edit({ solicitacaoFields }) {
  const { auditoresOptions, auditoresLoading, fetchAuditores } = useFetchDataAuditores();
  const {
    comunicadoOptions,
    solicitacaoOptions,
    relatorioOptions,
    fetchTemplates,
  } = useFetchDataTemplatesEmails();

  const { setValue, watch, reset } = useFormContext();
  const values = watch();

  const disabledInputs = useDisableIfStatusAndRole(
    solicitacaoFields?.id,
    [2, 3, 4, 5, 6, 7],
    perfisAdmin
  );

  const statusSolicitacao = useSelector(selectStatusById(solicitacaoFields?.id));

  const hiddenField = () => {
    const tipoServico = solicitacaoFields?.compliance?.flagAuditoria;

    return statusSolicitacao < 4 || tipoServico !== true;
  };

  const textareaHeight = hiddenField() ? 80 : 120;

  // 1) Fetch inicial
  useEffect(() => {
    fetchAuditores();
    fetchTemplates();
  }, []);

  // 2) Reset inicial com valores default 
  useEffect(() => {
    if (!solicitacaoFields) return;

    reset({
      numeroSolicitacao: solicitacaoFields.numeroSolicitacao ?? '',
      nomeUnidadeGerencia: solicitacaoFields.unidadeGerencia?.nome ?? '',
      nomeGerente: solicitacaoFields.unidadeGerencia?.nomeGerente ?? '',
      nomeEncarregado: solicitacaoFields.unidadeGerencia?.nomeEncarregado ?? '',
      nomeAuditor: solicitacaoFields.auditor ?? '',
      emailCC: solicitacaoFields.emailCC ?? '',
      templateEmailComunicado: solicitacaoFields.templateEmailComunicado ?? '',
      templateEmailSolicitacao: solicitacaoFields.templateEmailSolicitacao ?? '',
      templateEmailRelatorio: solicitacaoFields.templateEmailRelatorio ?? '',
      dataSolicitacao: solicitacaoFields.dataSolicitacao ?? '',
      prazoSolicitacao: solicitacaoFields.prazoSolicitacao ?? '',
      dataLimiteSolicitacao: solicitacaoFields.dataLimiteSolicitacao ?? '',
      dataAgendarEnvio: solicitacaoFields.dataAgendarEnvio ?? null,
      observacoes: solicitacaoFields.observacoes ?? '',
      criadoPorUsuario: solicitacaoFields.criadoPorUsuario ?? '',
    });
  }, [solicitacaoFields, reset]);


  // 3) Atualiza data limite
  useEffect(() => {
    const prazo = parseInt(values.prazoSolicitacao ?? '', 10);
    const base = values.dataAgendarEnvio
      ? new Date(values.dataAgendarEnvio)
      : new Date(values.dataSolicitacao);
    if (!isNaN(prazo) && prazo > 0 && prazo <= 365) {
      base.setDate(base.getDate() + prazo);
      setValue('dataLimiteSolicitacao', base.toISOString().split('T')[0]);
    } else if (prazo > 365) {
      SweetAlert.error('Prazo Inválido', 'O limite para o prazo é de 365 dias.');
      setValue('prazoSolicitacao', '');
      setValue('dataLimiteSolicitacao', '');
    } else {
      setValue('dataLimiteSolicitacao', '');
    }
  }, [
    values.prazoSolicitacao,
    values.dataAgendarEnvio,
    values.dataSolicitacao,
    setValue,
  ]);

  // 4) Handler para data agendamento
  const handleDataAgendarEnvioChange = useCallback(
    (date) => {
      if (!date) {
        setValue('dataAgendarEnvio', '');
        return;
      }
      const hoje = dayjs().startOf('day');
      if (!date.startOf('day').isAfter(hoje)) {
        SweetAlert.error(
          'Data inválida',
          'A data de agendamento não pode ser hoje nem no passado.'
        );
        setValue('dataAgendarEnvio', '');
        return;
      }
      setValue('dataAgendarEnvio', date.format('YYYY-MM-DD'));
    },
    [setValue]
  );


  const formatIfEmpty = (val = '') => {
    return disabledInputs && (val === '' || val == null) ? '-' : val;
  }

  return (
    <div className="form">
      <div className="row mt-3">
        <div className="col-7 p-0">

          <InputTextComp
            label="Solicitação Nº"
            hoverMessage="Número da solicitação"
            value={values.numeroSolicitacao ?? ''}
            disabled
          />

          <InputTextComp
            label="Unidade/Gerência"
            hoverMessage="Nome Unidade/Gerência"
            value={values.nomeUnidadeGerencia ?? ''}
            disabled
          />

          <InputTextComp
            label="Gerente"
            hoverMessage="Nome do(a) gerente da Unidade/Gerência"
            value={values.nomeGerente ?? ''}
            disabled
          />

          <InputTextComp
            label="Encarregado(a)"
            hoverMessage="Nome do(a) encarregado(a) da Unidade/Gerência"
            value={values.nomeEncarregado ?? ''}
            disabled
          />

          <SelectOptionComp
            label="Auditor(a)"
            value={values.nomeAuditor ?? ''}
            options={auditoresOptions}
            onChange={(o) => setValue('nomeAuditor', o)}
            placeholder="Selecionar um auditor(a)"
            hoverMessage="Selecionar um auditor(a)"
            isLoading={auditoresLoading}
            isDisabled={disabledInputs}
            required
          />

          <InputTextComp
            label="Enviar E-mail CC"
            value={formatIfEmpty(values.emailCC)}
            onChange={(e) => setValue('emailCC', e.target.value)}
            placeholder="E-mails CC (opcional)"
            hoverMessage="E-mails separados por ponto e vírgula (;). (Opcional)"
            disabled={disabledInputs}
          />
        </div>

        <div className="col-5 p-0">

          <InputTextComp
            label="Criado Por Usuário"
            hoverMessage="Usuário responsável por criar a solicitação"
            value={values.criadoPorUsuario ?? ''}
            disabled
          />

          <InputTextComp
            label="Data Solicitação"
            hoverMessage="Data em que a solicitação foi criada"
            value={values.dataSolicitacao ?? ''}
            type="date"
            disabled
            required
          />

          <InputTextComp
            label="Prazo (dias)"
            hoverMessage="Prazo para receber uma resposta da solicitação"
            value={values.prazoSolicitacao ?? ''}
            onChange={(e) => setValue('prazoSolicitacao', e.target.value)}
            type="number"
            onInput={(e) => {
              const v = e.target.value.replace(/\D/g, '').slice(0, 3);
              e.target.value = v.replace(/^0+/, '') || '';
            }}
            placeholder="0"
            required
            disabled={disabledInputs}
          />

          <InputTextComp
            label="Data Limite"
            hoverMessage="Data limite para responder a solicitação"
            value={values.dataLimiteSolicitacao ?? ''}
            type="date"
            disabled
            required
          />

          <InputDatePickerComp
            label="Agendar Envio"
            name="dataAgendarEnvio"
            value={
              values.dataAgendarEnvio
                ? dayjs(values.dataAgendarEnvio, 'YYYY-MM-DD')
                : null
            }
            placeholder={disabledInputs && !values.dataAgendarEnvio ? '-' : undefined}
            onChange={handleDataAgendarEnvioChange}
            hoverMessage="O horário predefinido no sistema para agendamento de envio é 07:00"
            format="DD/MM/YYYY"
            showToday={false}
            disabled={disabledInputs}
          />

        </div>
      </div>

      {/* TITULOS ALINHADOS */}
      <div className="row mt-3 align-items-center mt-5">
        <div className="col-12 col-md-7 d-flex align-items-center">
          <h2> Templates de e-mails</h2>
        </div>
        <div className="col-12 col-md-5 d-flex align-items-center">
          <h2> Observações (Texto Simples)</h2>
        </div>
      </div>

      {/* CAMPOS DE TEMPLATES E TEXTAREA */}
      <div className="row">
        <div className="col-7 p-0">
          <SelectOptionComp
            label="Comunicado"
            hoverMessage="Selecione um template de e-mail para comunicado"
            options={comunicadoOptions}
            value={values.templateEmailComunicado ?? ''}
            onChange={(o) => setValue('templateEmailComunicado', o)}
            placeholder={"Selecione um template"}
            required
            isDisabled={disabledInputs}
          />

          <SelectOptionComp
            label="Solicitação"
            hoverMessage="Selecione um template de e-mail para solicitação"
            options={solicitacaoOptions}
            value={values.templateEmailSolicitacao ?? ''}
            onChange={(o) => setValue('templateEmailSolicitacao', o)}
            required
            isDisabled={disabledInputs}
          />

          <SelectOptionComp
            label="Relatório"
            hoverMessage="Selecione um template de e-mail para relatório"
            options={relatorioOptions}
            value={values.templateEmailRelatorio ?? ''}
            onChange={(o) => setValue('templateEmailRelatorio', o)}
            placeholder={"Selecione um template"}
            required
            isDisabled={statusSolicitacao > 4}
            hidden={hiddenField()}
          />
        </div>

        <div className="col-5 p-0">
          <textarea
            className="form-control"
            value={formatIfEmpty(values.observacoes)}
            onChange={(e) => setValue('observacoes', e.target.value)}
            placeholder="Digite observações (opcional)"
            title="Digite obervações acerca da solicitação. (Opcional)"
            style={{
              height: textareaHeight, // ajusta altura baseado na visibilidade do campo Relatório
              resize: 'none',
            }}
            disabled={disabledInputs}
          />
        </div>
      </div>
    </div>
  );
}

export default Edit;